import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'

import { useFlags } from 'launchdarkly-react-client-sdk'
import isEmpty from 'lodash/isEmpty'

import {
  DiningOptionBehavior,
  PackagingInfo,
  PlaceCashOrderInput,
  PlaceCcOrderInput,
  PlaceCcOrderMutation,
  PlacePaidOrderInput,
  PlaceOrderErrorCode,
  usePlaceCashOrderMutation,
  usePlaceCcOrderMutation,
  usePlacePaidOrderMutation,
  usePlaceSpiOrderMutation,
  PlaceSpiOrderInput
} from 'src/apollo/onlineOrdering'
import { reportErrorMessageWithData } from 'src/lib/js/clientError'
import { useIsIntlRestaurant } from 'src/lib/js/hooks/useIsIntlRestaurant'
import { useDelivery } from 'public/components/online_ordering/DeliveryContext'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { useSift } from 'public/components/default_template/meta/sift/SiftContext'
import {
  getCallingCodeForOrder,
  getNationalPhoneNumberForOrder,
  getPaymentOption,
  applyContactlessIndicator
} from 'public/components/default_template/online_ordering/checkout/checkoutUtils'
import { getAppliedFundedOffersTotal } from 'public/components/default_template/offers/appliedFundedOffers'
import { useRefreshToastCashAmount } from 'public/components/default_template/online_ordering/checkout/payment/ToastCashPaymentToggle'
import {
  useServerSpi,
  useSpi,
  useSpiEventTracker
} from 'public/components/default_template/online_ordering/checkout/payment/useSpi'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import { useGiftCard } from 'public/components/online_ordering/GiftCardContext'
import {
  PaymentOption,
  usePayment,
  PaymentCompletedParams
} from 'public/components/online_ordering/PaymentContext'

import { getDigitalSurface, getIsCustomDomain } from './attributionUtils'

type PlaceOrderResponse = PlaceCcOrderMutation['placeOrder'] & {
  __typename: 'PlaceOrderResponse'
}
type CompletedOrder = PlaceOrderResponse['completedOrder']

export type CheckoutFormData = {
  yourInfoFirstName: string
  yourInfoLastName: string
  yourInfoEmail: string
  yourInfoPhone: string
  curbsidePickup: boolean
  curbsidePickupVehicle?: string
  curbsidePickupVehicleColor?: string
  deliveryInstructions?: string
  deliveryZipCode?: string
  semiPaymentIntentId?: string
  yourInfoCountryCode?: string
}

export type OrderError = {
  message: string
  type: string
  refetchCart?: boolean
  paymentWasConfirmed?: boolean
}

export type CompleteOrderHandler = (
  order: CompletedOrder | undefined | null
) => void

export type ApmAuthResult = {
  apmName: string
  canPlaceOrder: boolean
  criticalError: boolean
  token?: string
}

export type CheckoutContextType = {
  createAccount: boolean
  setCreateAccount: (create: boolean) => void
  saveNewAddress: boolean
  setSaveNewAddress: (save: boolean) => void
  contactlessDelivery: boolean
  setContactlessDelivery: (contactless: boolean) => void
  giftCardAppliedAmount: number
  toastCashAppliedAmount: number
  orderTotal: number
  placeOrder: (
    cartGuid: string,
    checkoutFormData: CheckoutFormData,
    completedOrderHandler: CompleteOrderHandler,
    paymentId?: string,
    paymentCompletedParams?: PaymentCompletedParams | null
  ) => Promise<CompletedOrder | undefined | null>
  orderError: OrderError | null
  setOrderError: (err: OrderError | null) => void
  enabledPaymentOptions: Set<PaymentOption>
  packagingOptions: PackagingInfo
  setPackagingOptions: (packagingOptions: PackagingInfo) => void
  showAdyenOverlay: boolean
  setShowAdyenOverlay: (show: boolean) => void
  showTipWarning: boolean
  setShowTipWarning: (showTipWarning: boolean) => void
  autoPromptAuth: boolean
  setAutoPromptAuth: (autoPromptAuth: boolean) => void
  reviewModalOpen: boolean
  setReviewModalOpen: (open: boolean) => void
  disableAccountCreation: boolean
  setDisableAccountCreation: (disable: boolean) => void
}

export const CheckoutContext = createContext<CheckoutContextType | undefined>(
  undefined
)

const roundToCents = (amount: number) => Math.round(amount * 100) / 100

export const CheckoutContextProvider = (props: React.PropsWithChildren<{}>) => {
  const { sessionId } = useSift()
  const { spiSurchargingEnabled } = useSpi()
  const { serverSpiEnabled } = useServerSpi()
  const isIntlRestaurant = useIsIntlRestaurant()
  const { trackSpi } = useSpiEventTracker()
  const { ooRestaurant, toastProduct } = useRestaurant()
  const { cart, refetchCart, channel } = useCart()
  const {
    giftCardNumber,
    giftCardGuid,
    verificationCode,
    giftCardAvailableBalance
  } = useGiftCard()
  const { refreshAuthToken } = useCustomer()
  const fundedOffersTotal = getAppliedFundedOffersTotal(
    cart?.appliedFundedOffers
  )
  const giftCardAppliedAmount = useMemo(
    () =>
      roundToCents(
        Math.min(
          giftCardAvailableBalance,
          Math.max((cart?.order?.totalV2 || 0) - fundedOffersTotal, 0)
        )
      ),
    [giftCardAvailableBalance, cart?.order, fundedOffersTotal]
  )
  const [showTipWarning, setShowTipWarning] = useState(false)

  const [placeCashOrderMutation] = usePlaceCashOrderMutation()
  const [placeCcOrderMutation] = usePlaceCcOrderMutation()
  const [placePaidOrderMutation] = usePlacePaidOrderMutation()
  const [placeSpiOrderMutation] = usePlaceSpiOrderMutation()
  const [orderError, setOrderError] = useState<OrderError | null>(null)
  const [createAccount, setCreateAccount] = useState(false)
  const [saveNewAddress, setSaveNewAddress] = useState(false)
  const [contactlessDelivery, setContactlessDelivery] = useState(false)
  const [autoPromptAuth, setAutoPromptAuth] = useState(true)
  const [disableAccountCreation, setDisableAccountCreation] = useState(false)
  const { sessionAddressLabel, saveSessionAddress } = useDelivery()
  const [reviewModalOpen, setReviewModalOpen] = useState(false)

  // Payment
  const {
    paymentOption,
    tipEnabled,
    tipAmount,
    fundraisingAmount,
    paymentType,
    paymentIntent,
    toastCashInfo,
    newCreditCard
  } = usePayment()
  const toastCashAppliedAmount = useMemo(
    () =>
      roundToCents(
        Math.min(
          toastCashInfo?.toastCashAvailableAmount ?? 0,
          Math.max(
            (cart?.order?.totalV2 || 0) +
              tipAmount -
              giftCardAppliedAmount -
              fundedOffersTotal,
            0
          )
        )
      ),
    [
      toastCashInfo?.toastCashAvailableAmount,
      cart?.order,
      tipAmount,
      giftCardAppliedAmount,
      fundedOffersTotal
    ]
  )
  const {
    ooToastCashSpend: ooToastCashSpendEnabled,
    ooAcceptImportedGiftcards
  } = useFlags()
  const refreshToastCashAmount = useRefreshToastCashAmount()

  const enabledPaymentOptions = useMemo(() => {
    const enabledOptions = new Set<PaymentOption>()

    const canPayLater = (cart?.paymentOptions.uponReceipt.length || 0) > 0
    const canPayNow = (cart?.paymentOptions.atCheckout.length || 0) > 0

    if (canPayLater) {
      enabledOptions.add(PaymentOption.UponReceipt)
    }
    if (canPayNow) {
      enabledOptions.add(PaymentOption.PayNow)
    }
    return enabledOptions
  }, [
    cart?.paymentOptions.uponReceipt.length,
    cart?.paymentOptions.atCheckout.length
  ])

  // PackagingOptions
  const [packagingOptions, setPackagingOptions] = useState<PackagingInfo>({
    packagingItems: []
  } as PackagingInfo)

  // Return new delivery address if the customer has provided an apt number or delivery instructions
  // during checkout. Also return data if the customer wants to save their address. The addressLabel
  // field only appears for logged in customers.
  const getNewDeliveryAddress = useCallback(
    (checkoutFormData: CheckoutFormData, contactlessDelivery: boolean) => {
      if (cart?.diningOptionBehavior === DiningOptionBehavior.Delivery) {
        const saveAddressFields =
          saveSessionAddress && sessionAddressLabel
            ? {
                name: sessionAddressLabel,
                saveAddress: saveSessionAddress
              }
            : {}

        const notes =
          checkoutFormData.deliveryInstructions ||
          cart?.order?.deliveryInfo?.notes
        const finalNotes =
          contactlessDelivery && notes
            ? applyContactlessIndicator(notes)
            : notes

        if (
          !isEmpty(saveAddressFields) ||
          checkoutFormData.deliveryInstructions ||
          checkoutFormData.deliveryZipCode
        ) {
          return {
            newAddress: {
              deliveryInfo: {
                address1: cart?.order?.deliveryInfo?.address1,
                address2: cart?.order?.deliveryInfo?.address2,
                city: cart?.order?.deliveryInfo?.city,
                state: cart?.order?.deliveryInfo?.state,
                zipCode:
                  checkoutFormData.deliveryZipCode ||
                  cart?.order?.deliveryInfo?.zipCode,
                latitude: cart?.order?.deliveryInfo?.latitude,
                longitude: cart?.order?.deliveryInfo?.longitude,
                administrativeArea:
                  cart?.order?.deliveryInfo?.administrativeArea,
                country: cart?.order?.deliveryInfo?.country,
                notes: finalNotes
              },
              ...saveAddressFields
            }
          }
        }
      }

      return {}
    },
    [cart, saveSessionAddress, sessionAddressLabel]
  )

  const handlePlaceOrderError = (
    response: any,
    completeOrderHandler: CompleteOrderHandler
  ) => {
    if (response?.data?.placeOrder.__typename === 'PlaceOrderResponse') {
      completeOrderHandler(response.data.placeOrder.completedOrder)
    } else if (response?.data?.placeOrder.__typename === 'PlaceOrderError') {
      const errorData = {
        type: response.data.placeOrder.placeOrderErrorCode,
        message: response.data.placeOrder.message
      }
      if (
        errorData.type === PlaceOrderErrorCode.CriticalError ||
        errorData.type === PlaceOrderErrorCode.PlaceOrderFailed
      ) {
        reportErrorMessageWithData('Error placing order', errorData)
        // A CriticalError may occur because of an expired auth token, so try refreshing it.
        refreshAuthToken()
      }
      let paymentWasConfirmed = false
      if (paymentOption === PaymentOption.PayNow) {
        paymentWasConfirmed = true
      }
      setOrderError({ ...errorData, paymentWasConfirmed })
      if (
        errorData.type ===
          PlaceOrderErrorCode.GuestCurrencyAccountInsufficientFunds ||
        errorData.type === PlaceOrderErrorCode.GuestCurrencyAccountPaymentDenied
      ) {
        // On error, also re-hydrate guest wallet data
        if (ooToastCashSpendEnabled) {
          refreshToastCashAmount()
        }
      }
      if (!isIntlRestaurant) {
        trackSpi('temp_SPI_placeOrder_failed', {
          message: response?.data?.placeOrder?.message,
          paymentIntentId: paymentIntent?.id,
          paymentType: getPaymentOption(
            paymentType,
            paymentOption,
            giftCardAppliedAmount,
            orderTotal,
            isIntlRestaurant
          )
        })
      }
    } else if (
      response?.data?.placeOrder.__typename === 'PlaceOrderCartUpdatedError'
    ) {
      refetchCart()
      setOrderError({
        type: response.data.placeOrder.placeOrderCartUpdatedErrorCode,
        message: response.data.placeOrder.message
      })
      if (!isIntlRestaurant) {
        trackSpi('temp_SPI_placeOrder_failed', {
          message: response.data.placeOrder.message,
          paymentIntentId: paymentIntent?.id,
          paymentType: getPaymentOption(
            paymentType,
            paymentOption,
            giftCardAppliedAmount,
            orderTotal,
            isIntlRestaurant
          )
        })
      }
    }
  }

  /**
   * Place order using placeCashOrderMutation, for orders which don't require a payment.
   * Helper function for placeOrder, not for direct use.
   *
   * @param commonInputData - The common input data for the order.
   * @returns response from placeCashOrderMutation.
   */
  const placeCashOrder = async (commonInputData: PlaceCashOrderInput) =>
    await placeCashOrderMutation({ variables: { input: commonInputData } })

  /**
   * Place order for US restaurants. Helper function for placeOrder, not for direct use.
   *
   * With server-side SPI enabled, the server confirms the payment as part of
   * placeSpiOrder. Otherwise the payment was already confirmed client-side and
   * the order is placed via placePaidOrder.
   *
   * @param commonInputData - The common input data for the order.
   * @param paymentId - The SPI payment intent ID for the order.
   * @param paymentCompletedParams - The completed SPI payment params for the order.
   * @returns response from the place order mutation, or null if the order could not be placed.
   */
  const placeOrderForUS = async (
    commonInputData: any,
    paymentId?: string,
    paymentCompletedParams?: PaymentCompletedParams | null
  ) => {
    const orderInput = commonInputData as
      | PlacePaidOrderInput
      | PlaceSpiOrderInput
    orderInput.tipAmount = tipAmount
    orderInput.deliveryCommunicationConsentGiven = true
    if (fundraisingAmount) {
      orderInput.fundraisingInput = { fundraisingAmount }
    }
    if (toastCashAppliedAmount && toastCashInfo?.toastCashAccountId) {
      orderInput.guestCurrencyAccountPayment = {
        expectedGuestCurrencyAccountPaymentAmount: toastCashAppliedAmount,
        guestCurrencyAccountId: toastCashInfo.toastCashAccountId
      }
    }

    let placeOrderResponse
    if (serverSpiEnabled) {
      const { paymentMethodId, sessionSecret } = paymentCompletedParams ?? {}

      const spiOrderInput = orderInput as PlaceSpiOrderInput

      if (!!paymentId && !!paymentMethodId && !!sessionSecret) {
        spiOrderInput.spiPaymentData = {
          paymentIntentId: paymentId,
          paymentMethodId,
          sessionSecret,
          saveCard: newCreditCard?.saveCard || false,
          ccFraudSessionId: sessionId
        }
        if (spiSurchargingEnabled) {
          spiOrderInput.surchargeAmount =
            paymentCompletedParams?.surchargeAmount
        }
      }

      placeOrderResponse = await placeSpiOrderMutation({
        variables: { input: spiOrderInput },
        context: {
          headers: { 'Toast-Restaurant-External-ID': ooRestaurant?.guid }
        }
      })
    } else {
      const paidOrderInput = orderInput as PlacePaidOrderInput
      // paymentId may be undefined if a gift card, loyalty, or promo code covers the entire check amount.
      paidOrderInput.paymentId = paymentId
      if (spiSurchargingEnabled) {
        paidOrderInput.paymentMethodId = paymentCompletedParams?.paymentMethodId
        paidOrderInput.surchargeAmount = paymentCompletedParams?.surchargeAmount
      }

      placeOrderResponse = await placePaidOrderMutation({
        variables: { input: paidOrderInput },
        context: {
          headers: { 'Toast-Restaurant-External-ID': ooRestaurant?.guid }
        }
      })
    }

    trackSpi('temp_SPI_order_placed', {
      paymentType,
      placeOrderResponseType: placeOrderResponse?.data?.placeOrder?.__typename
    })
    return placeOrderResponse
  }

  /**
   * Place order using placeCcOrderMutation, for international restaurants.
   * Helper function for placeOrder, not for direct use.
   *
   * @param commonInputData - The common input data for the order.
   * @param semiPaymentIntentId - The semi-integrated (Adyen) payment intent ID for the order.
   * @returns response from placeCcOrderMutation.
   */
  const placeOrderForIntl = async (
    commonInputData: any,
    semiPaymentIntentId?: string
  ) => {
    const orderInput = commonInputData as PlaceCcOrderInput
    orderInput.deliveryCommunicationConsentGiven = true
    orderInput.tipAmount = tipAmount
    if (fundraisingAmount) {
      orderInput.fundraisingInput = { fundraisingAmount }
    }

    if (semiPaymentIntentId) {
      orderInput.semiPaymentIntentId = semiPaymentIntentId
    }

    return await placeCcOrderMutation({
      variables: { input: orderInput },
      context: {
        headers: { 'Toast-Restaurant-External-ID': ooRestaurant?.guid }
      }
    })
  }

  const placeOrder = async (
    cartGuid: string,
    checkoutFormData: CheckoutFormData,
    completeOrderHandler: CompleteOrderHandler,
    paymentId?: string,
    paymentCompletedParams?: PaymentCompletedParams | null
  ) => {
    const curbsidePickupSelected =
      cart?.diningOptionBehavior === DiningOptionBehavior.TakeOut &&
      checkoutFormData.curbsidePickup

    const commonInputData = {
      cartGuid,
      customer: {
        firstName: checkoutFormData.yourInfoFirstName,
        lastName: checkoutFormData.yourInfoLastName,
        email: checkoutFormData.yourInfoEmail,
        phone: getNationalPhoneNumberForOrder(checkoutFormData.yourInfoPhone),
        phoneCountryCode:
          checkoutFormData.yourInfoCountryCode ??
          getCallingCodeForOrder(checkoutFormData.yourInfoPhone)
      },
      ...(ooRestaurant?.packagingConfig?.enabled && {
        packagingInfo: packagingOptions
      }),
      ...getNewDeliveryAddress(checkoutFormData, contactlessDelivery),
      ...(giftCardNumber &&
        giftCardAppliedAmount && {
          giftCard: {
            cardNumber: giftCardNumber.replace(/\s/g, ''),
            expectedAvailableBalance: giftCardAppliedAmount,
            verificationCode,
            ...(ooAcceptImportedGiftcards &&
              giftCardGuid && { cardGuid: giftCardGuid })
          }
        }),
      ...(curbsidePickupSelected &&
        checkoutFormData.curbsidePickupVehicle && {
          [serverSpiEnabled ? 'curbsidePickup' : 'curbsidePickupV2']: {
            transportDescription: checkoutFormData.curbsidePickupVehicle,
            ...(checkoutFormData.curbsidePickupVehicleColor && {
              transportColor: checkoutFormData.curbsidePickupVehicleColor
            })
          }
        }),
      ...(curbsidePickupSelected &&
        !serverSpiEnabled &&
        !checkoutFormData.curbsidePickupVehicle && {
          curbsidePickup: { selected: true }
        }),
      digitalSurface: getDigitalSurface(toastProduct, channel),
      isCustomDomain:
        typeof window !== 'undefined'
          ? getIsCustomDomain(window.location.hostname)
          : null
    }

    // If user selected "pay at restaurant", should be a cash order.
    // If orderTotal is 0, no payment is required. This case is handled by placePaidOrder, which is used for US restaurants.
    // For international restaurants, if orderTotal is 0, we place a cash order because placeCcOrder has validation which requires a payment method to be attached.
    const shouldUseCashOrderMutation =
      paymentOption !== PaymentOption.PayNow ||
      (isIntlRestaurant && orderTotal === 0)

    let response = null
    if (shouldUseCashOrderMutation) {
      response = await placeCashOrder(commonInputData as PlaceCashOrderInput)
    } else if (isIntlRestaurant) {
      response = await placeOrderForIntl(
        commonInputData,
        checkoutFormData.semiPaymentIntentId
      )
    } else {
      response = await placeOrderForUS(
        commonInputData,
        paymentId,
        paymentCompletedParams
      )
    }

    handlePlaceOrderError(response, completeOrderHandler)
    return null
  }

  const orderTotal = useMemo(
    () =>
      Math.max(
        roundToCents(
          Number(cart?.order?.totalV2 || 0) +
            (tipEnabled ? tipAmount : 0) +
            fundraisingAmount -
            giftCardAppliedAmount -
            toastCashAppliedAmount -
            fundedOffersTotal
        ),
        0
      ),
    [
      cart?.order?.totalV2,
      fundedOffersTotal,
      tipEnabled,
      tipAmount,
      fundraisingAmount,
      giftCardAppliedAmount,
      toastCashAppliedAmount
    ]
  )

  const [showAdyenOverlay, setShowAdyenOverlay] = useState<boolean>(false)

  return (
    <CheckoutContext.Provider
      value={{
        createAccount,
        setCreateAccount,
        saveNewAddress,
        setSaveNewAddress,
        contactlessDelivery,
        setContactlessDelivery,
        giftCardAppliedAmount,
        toastCashAppliedAmount,
        orderTotal,
        placeOrder,
        orderError,
        setOrderError,
        enabledPaymentOptions,
        packagingOptions,
        setPackagingOptions,
        showAdyenOverlay,
        setShowAdyenOverlay,
        showTipWarning,
        setShowTipWarning,
        autoPromptAuth,
        setAutoPromptAuth,
        reviewModalOpen,
        setReviewModalOpen,
        disableAccountCreation,
        setDisableAccountCreation
      }}
    >
      {props.children}
    </CheckoutContext.Provider>
  )
}

export const useCheckout = () => {
  const context = useContext(CheckoutContext)
  if (!context) {
    throw new Error('useCheckout must be used within a CheckoutContextProvider')
  }

  return context
}
